/**
 * BB Product Page Controller v4
 * ─────────────────────────────
 * Unified bundle confirmation + ATC intercept system.
 *
 * Key changes from v3:
 * - Detects bundle confirmation via cart state polling (Appstle-proof)
 * - Opens cart drawer using Dawn's native .open() after section refresh
 * - Single ATC intercept system (replaces dual click/submit approach)
 * - Adds .bb-bundle-mode class to product-info for CSS hooks
 *
 * Survives Shopify section re-renders via document-level MutationObserver.
 */
(function () {
  'use strict';

  /* ══════════════════════════════════════
     Constants
     ══════════════════════════════════════ */

  var SELLING_PLANS = {
    '1 Pack': [9080734003, 9080766771, 9080897843],
    '2 Pack': [9080799539, 9080832307, 9080865075],
    '3 Pack': [9080930611, 9080963379, 9080996147]
  };

  // Prices are read from the Liquid-rendered data-pack-prices element (region/currency-aware)
  var PACK_PRICES = (function () {
    var el = document.querySelector('[data-pack-prices]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }());

  var BUNDLE_IDS = {
    '2 Pack': { onetime: 'cfki65v2i5', subscribe: '70glcwdm4o' },
    '3 Pack': { onetime: 'upaqnbgsmu', subscribe: 'yqrwlokc7z' }
  };

  /* ══════════════════════════════════════
     State
     ══════════════════════════════════════ */

  var state = {
    purchaseType: 'subscribe',
    frequencyIndex: 0,
    flavour: '',
    bundleConfirmed: false   // Set true when Appstle adds items to cart
  };

  var isUpdating = false;
  var boundElements = new WeakSet();

  /* ══════════════════════════════════════
     Helpers
     ══════════════════════════════════════ */

  var MONEY_FORMAT = (function () {
    var el = document.querySelector('[data-money-format]');
    if (!el) return '${{amount}}';
    try { return JSON.parse(el.textContent); } catch (e) { return '${{amount}}'; }
  }());

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var amountNoDec = Math.round(cents / 100).toString();
    return MONEY_FORMAT
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDec)
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDec)
      .replace('{{amount_with_apostrophe_separator}}', amount.replace('.', "'"));
  }

  function getSelectedPack() {
    var el = document.querySelector('.bb-pack-selector input[type="radio"]:checked');
    return el ? el.value : '1 Pack';
  }

  function getQuantity() {
    var el = document.querySelector('quantity-input input[name="quantity"]');
    return el ? Math.max(1, parseInt(el.value, 10) || 1) : 1;
  }

  function getSelectedFlavour() {
    var el = document.querySelector('.bb-flavour-selector input[type="radio"]:checked');
    return el ? el.value : '';
  }

  /**
   * Returns true when Mix & Match is the active flavour on a 2+ Pack.
   */
  function isMixMatchActive() {
    var flavour = state.flavour || getSelectedFlavour();
    var pack = getSelectedPack();
    return flavour === 'Mix & Match' && !!BUNDLE_IDS[pack];
  }

  /**
   * Get the active Appstle bundle reference ID for current pack + purchase type.
   */
  function getActiveBundleId() {
    var pack = getSelectedPack();
    var config = BUNDLE_IDS[pack];
    if (!config) return null;
    return state.purchaseType === 'subscribe' ? config.subscribe : config.onetime;
  }

  /**
   * Sort Appstle's frequency <option> elements into ascending numeric order.
   * Extracts the number from each option text (e.g. "Deliver every 12 weeks" → 12)
   * and sorts lowest to highest. Only runs once per <select>.
   */
  function sortFrequencyOptions(container) {
    var select = container.querySelector('select');
    if (!select || select._bbSorted) return;
    var opts = Array.from(select.options);
    opts.sort(function (a, b) {
      var numA = parseInt((a.text.match(/\d+/) || [0])[0], 10);
      var numB = parseInt((b.text.match(/\d+/) || [0])[0], 10);
      return numA - numB;
    });
    opts.forEach(function (opt) { select.appendChild(opt); });

    // Pre-select default frequency based on pack size
    var pack = getSelectedPack();
    var targetWeeks = (pack === '3 Pack') ? 12 : 8; // 3 Pack → 12 weeks, 2 Pack → 8 weeks
    for (var i = 0; i < select.options.length; i++) {
      var num = parseInt((select.options[i].text.match(/\d+/) || [0])[0], 10);
      if (num === targetWeeks) {
        select.selectedIndex = i;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }

    select._bbSorted = true;
  }

  /**
   * Returns true when the active Appstle bundle still needs items.
   */
  function isBundleIncomplete() {
    if (!isMixMatchActive()) return false;
    var bid = getActiveBundleId();
    if (!bid) return true;
    var bundleEl = document.querySelector('[appstle-dynamic-bundle-unique-reference="' + bid + '"]');
    if (!bundleEl) return true;
    var msg = bundleEl.querySelector('.ab-bundle-required-product-validation-message');
    if (!msg) return false;
    if (msg.offsetParent === null || msg.style.display === 'none') return false;
    var text = msg.textContent.trim();
    return text !== '' && text !== '0';
  }

  /* ══════════════════════════════════════
     Price Management
     ══════════════════════════════════════ */

  function applyPrices() {
    if (isUpdating) return;
    isUpdating = true;

    var pack = getSelectedPack();
    var type = state.purchaseType;
    var freqIndex = state.frequencyIndex;
    var prices = PACK_PRICES[pack];
    var plans = SELLING_PLANS[pack];

    if (!prices || !plans) {
      isUpdating = false;
      return;
    }

    var otp = document.querySelector('[data-onetime-price]');
    if (otp) otp.textContent = formatMoney(prices.onetime);

    var subp = document.querySelector('[data-subscribe-price]');
    if (subp) subp.textContent = formatMoney(prices.subscribe);

    var sav = document.querySelector('[data-subscribe-savings]');
    if (sav) {
      var saved = prices.onetime - prices.subscribe;
      sav.textContent = saved > 0
        ? 'Subscribe and save ' + formatMoney(saved)
        : 'Subscribe and save';
    }

    var spi = document.querySelector('[data-selling-plan-input]');
    if (spi) {
      spi.value = type === 'subscribe' ? (plans[freqIndex] || plans[0]) : '';
    }

    var pd = document.getElementById('price-display');
    if (pd) {
      var qty = getQuantity();
      var unitPrice = type === 'subscribe' ? prices.subscribe : prices.onetime;
      pd.textContent = formatMoney(unitPrice * qty);
    }

    // Restore subscribe toggle state (Shopify re-render resets radios)
    var subRadio = document.getElementById(getSubscribeRadioId());
    var otRadio = document.getElementById(getOnetimeRadioId());
    if (type === 'subscribe' && subRadio && !subRadio.checked) {
      subRadio.checked = true;
    } else if (type === 'onetime' && otRadio && !otRadio.checked) {
      otRadio.checked = true;
    }

    var freqEl = document.querySelector('[data-frequency-select]');
    if (freqEl && parseInt(freqEl.value, 10) !== freqIndex) {
      freqEl.value = freqIndex;
    }

    isUpdating = false;
    applyBundleDisplay();
  }

  /* ══════════════════════════════════════
     Flavour Management
     ══════════════════════════════════════ */

  function ensureFlavourSelected() {
    var pack = getSelectedPack();
    var flavour = getSelectedFlavour();

    if (pack === '1 Pack' && (!flavour || flavour === 'Mix & Match')) {
      var berryRadio = document.querySelector('.bb-flavour-selector input[type="radio"][value="Berry Bliss"]');
      if (berryRadio && !berryRadio.checked) {
        berryRadio.checked = true;
        state.flavour = 'Berry Bliss';
        berryRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /* ══════════════════════════════════════
     Bundle Display
     ══════════════════════════════════════ */

  function applyBundleDisplay() {
    var pack = getSelectedPack();
    var type = state.purchaseType;
    var flavour = state.flavour || getSelectedFlavour();

    var isMixMatch = flavour === 'Mix & Match';
    var bundleConfig = BUNDLE_IDS[pack];
    var activeBundleId = null;

    if (isMixMatch && bundleConfig) {
      activeBundleId = type === 'subscribe' ? bundleConfig.subscribe : bundleConfig.onetime;
    }

    // Collect all known bundle IDs
    var allBundleIds = [];
    Object.keys(BUNDLE_IDS).forEach(function (p) {
      allBundleIds.push(BUNDLE_IDS[p].onetime);
      allBundleIds.push(BUNDLE_IDS[p].subscribe);
    });

    // Hide all, then show the active one
    allBundleIds.forEach(function (bid) {
      var selectors = [
        '[appstle-dynamic-bundle-unique-reference="' + bid + '"]',
        '.appstleDynamicBundle-' + bid
      ];
      selectors.forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (el) {
          if (bid === activeBundleId) {
            el.classList.add('bb-bundle-active');
            el.classList.remove('bb-bundle-hidden');
          } else {
            el.classList.remove('bb-bundle-active');
            el.classList.add('bb-bundle-hidden');
          }
        });
      });
    });

    // Toggle bundle zone visibility
    var zone = document.querySelector('[data-bb-bundle-zone]');
    if (zone) {
      if (activeBundleId) {
        zone.removeAttribute('hidden');
        zone.classList.add('bb-bundle-zone--active');
      } else {
        zone.setAttribute('hidden', '');
        zone.classList.remove('bb-bundle-zone--active');
      }
    }

    // Toggle .bb-subscribe--bundle-mode on subscribe wrapper
    var subWrapper = document.querySelector('.bb-subscribe-wrapper');
    if (subWrapper) {
      if (isMixMatch && bundleConfig) {
        subWrapper.classList.add('bb-subscribe--bundle-mode');
      } else {
        subWrapper.classList.remove('bb-subscribe--bundle-mode');
      }
    }

    // Toggle .bb-bundle-mode on product-info (CSS uses this for qty/frequency hiding)
    var productInfo = document.querySelector('product-info') || document.querySelector('.product--information');
    if (productInfo) {
      if (isMixMatch && bundleConfig) {
        productInfo.classList.add('bb-bundle-mode');
      } else {
        productInfo.classList.remove('bb-bundle-mode');
      }
    }

    // Place compact subscribe toggle inside active bundle zone
    manageBundleSubscribeToggle(activeBundleId);
  }

  /* ══════════════════════════════════════
     Compact In-Bundle Subscribe Toggle
     ══════════════════════════════════════ */

  /**
   * Create the in-bundle OTP/Subscribe toggle, matching the outer toggle's style.
   * Reuses the same CSS classes for identical appearance.
   * Uses a separate radio name to avoid conflicts with the outer (hidden) toggle.
   * No frequency dropdown — Appstle's own dropdown in the Sub zone handles that.
   */
  function createBundleSubscribeToggle() {
    var container = document.createElement('div');
    container.id = 'bb-bundle-subscribe';
    container.className = 'bb-bundle-subscribe';

    var pack = getSelectedPack();
    var prices = PACK_PRICES[pack];
    var otpPrice = prices ? formatMoney(prices.onetime) : '';
    var subPrice = prices ? formatMoney(prices.subscribe) : '';
    var savedText = '';
    if (prices) {
      var saved = prices.onetime - prices.subscribe;
      if (saved > 0) savedText = 'Subscribe and save ' + formatMoney(saved);
    }

    container.innerHTML =
      '<fieldset class="bb-subscribe-toggle">' +
        '<legend class="visually-hidden">Purchase type</legend>' +

        /* One-time row */
        '<label class="bb-subscribe-option bb-subscribe-onetime" for="bb-bundle-otp">' +
          '<input type="radio" id="bb-bundle-otp" name="bb-bundle-purchase-type" value="onetime">' +
          '<span class="bb-subscribe-option-inner">' +
            '<span class="bb-subscribe-radio-dot"></span>' +
            '<span class="bb-subscribe-option-text">' +
              '<span class="bb-subscribe-option-price" data-bundle-otp-price>' + otpPrice + '</span>' +
              '<span class="bb-subscribe-option-subtitle">One-time purchase</span>' +
            '</span>' +
          '</span>' +
        '</label>' +

        /* Subscribe row — uses div (not label) so dropdown clicks don't toggle radio */
        '<div class="bb-subscribe-option bb-subscribe-sub" data-bb-bundle-sub-row>' +
          '<input type="radio" id="bb-bundle-sub" name="bb-bundle-purchase-type" value="subscribe" checked>' +
          '<span class="bb-subscribe-option-inner">' +
            '<span class="bb-subscribe-radio-dot"></span>' +
            '<span class="bb-subscribe-option-text">' +
              '<span class="bb-subscribe-option-price" data-bundle-sub-price>' + subPrice + '</span>' +
              '<span class="bb-subscribe-option-subtitle">' +
                'Choose delivery frequency. <span data-bundle-savings>' + savedText + '</span> ' +
                '<button type="button" class="bb-learn-more" data-bundle-learn-more>Learn more</button>' +
              '</span>' +
            '</span>' +
            '<span class="bb-subscribe-frequency-inline" data-bundle-freq-slot></span>' +
          '</span>' +
        '</div>' +

      '</fieldset>';

    // Set initial checked state
    var otpRadio = container.querySelector('#bb-bundle-otp');
    var subRadio = container.querySelector('#bb-bundle-sub');
    if (state.purchaseType === 'onetime' && otpRadio) {
      otpRadio.checked = true;
      subRadio.checked = false;
    }

    // Bind radio change handlers
    container.querySelectorAll('input[name="bb-bundle-purchase-type"]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        var newType = radio.value;
        if (newType === state.purchaseType) return;
        state.purchaseType = newType;
        state.bundleConfirmed = false;

        // Sync the outer (hidden) radio so selling plan stays correct
        if (newType === 'subscribe') {
          var outerSub = document.getElementById(getSubscribeRadioId());
          if (outerSub) outerSub.checked = true;
        } else {
          var outerOtp = document.getElementById(getOnetimeRadioId());
          if (outerOtp) outerOtp.checked = true;
        }

        applyPrices();
      });
    });

    // Bind Learn More button
    var learnMore = container.querySelector('[data-bundle-learn-more]');
    if (learnMore) {
      learnMore.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var modal = document.querySelector('[data-bb-subscription-modal]');
        if (modal) modal.removeAttribute('hidden');
      });
    }

    // Subscribe row is a <div> not <label>, so handle radio selection via JS click.
    // Clicks on the freq dropdown are excluded so they don't toggle the radio.
    var subRow = container.querySelector('[data-bb-bundle-sub-row]');
    if (subRow) {
      subRow.addEventListener('click', function (e) {
        // Don't toggle radio when clicking the frequency dropdown or learn more
        if (e.target.closest('[data-bundle-freq-slot]') || e.target.closest('[data-bundle-learn-more]')) return;
        var radio = subRow.querySelector('input[type="radio"]');
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    return container;
  }

  /**
   * Place the toggle above the Confirm button in the active bundle zone.
   * Updates prices, savings text, and checked state each time.
   * Called at the end of applyBundleDisplay().
   */
  function manageBundleSubscribeToggle(activeBundleId) {
    var toggle = document.getElementById('bb-bundle-subscribe');

    if (!activeBundleId) {
      // Return any relocated Appstle dropdown before removing toggle
      if (toggle) {
        var slot = toggle.querySelector('[data-bundle-freq-slot]');
        if (slot) {
          var dropdown = slot.querySelector('.ab-bundle-checkout-selling-plan-select-container');
          if (dropdown && dropdown._bbOriginalParent) {
            pauseObserver = true;
            dropdown._bbOriginalParent.appendChild(dropdown);
            setTimeout(function () { pauseObserver = false; }, 200);
          }
        }
        toggle.remove();
      }
      return;
    }

    var bundleEl = document.querySelector(
      '[appstle-dynamic-bundle-unique-reference="' + activeBundleId + '"]'
    );
    if (!bundleEl) return;

    var confirmBtn = bundleEl.querySelector('.ab-bundle-add-bundle-to-cart');
    if (!confirmBtn) return;

    // Create if doesn't exist
    if (!toggle) {
      toggle = createBundleSubscribeToggle();
    }

    var freqSlot = toggle.querySelector('[data-bundle-freq-slot]');

    // Check if toggle is already in the right place and dropdown already relocated
    // If so, just update text/state — skip DOM moves that would disrupt the dropdown
    var alreadyInPlace = toggle.parentNode === confirmBtn.parentNode;
    var dropdownAlreadyInSlot = freqSlot &&
      freqSlot.querySelector('.ab-bundle-checkout-selling-plan-select-container');

    if (!alreadyInPlace) {
      // Pause observer — DOM moves trigger mutations → observer → re-entry loop
      pauseObserver = true;

      // Return any previously relocated Appstle dropdown back to its original zone
      if (freqSlot) {
        var staleDropdown = freqSlot.querySelector('.ab-bundle-checkout-selling-plan-select-container');
        if (staleDropdown && staleDropdown._bbOriginalParent) {
          staleDropdown._bbOriginalParent.appendChild(staleDropdown);
        }
      }

      // Move toggle to the active zone's confirm button area
      var parent = confirmBtn.parentNode;
      if (parent) {
        parent.insertBefore(toggle, confirmBtn);
      }

      // Move Appstle's REAL frequency dropdown into our subscribe row slot
      if (freqSlot && bundleEl) {
        var appstleFreq = bundleEl.querySelector('.ab-bundle-checkout-selling-plan-select-container');
        if (appstleFreq) {
          if (!appstleFreq._bbOriginalParent) {
            appstleFreq._bbOriginalParent = appstleFreq.parentNode;
          }
          freqSlot.appendChild(appstleFreq);
          sortFrequencyOptions(appstleFreq);
        }
      }

      setTimeout(function () { pauseObserver = false; }, 200);

    } else if (!dropdownAlreadyInSlot && freqSlot && bundleEl) {
      // Toggle in place but dropdown not yet relocated (e.g. Appstle rendered late)
      pauseObserver = true;
      var appstleFreq = bundleEl.querySelector('.ab-bundle-checkout-selling-plan-select-container');
      if (appstleFreq) {
        if (!appstleFreq._bbOriginalParent) {
          appstleFreq._bbOriginalParent = appstleFreq.parentNode;
        }
        freqSlot.appendChild(appstleFreq);
        sortFrequencyOptions(appstleFreq);
      }
      setTimeout(function () { pauseObserver = false; }, 200);
    }

    // Sync radio checked state
    var otpRadio = toggle.querySelector('#bb-bundle-otp');
    var subRadio = toggle.querySelector('#bb-bundle-sub');
    if (otpRadio && subRadio) {
      otpRadio.checked = (state.purchaseType === 'onetime');
      subRadio.checked = (state.purchaseType === 'subscribe');
    }

    // Update prices + savings for current pack
    var pack = getSelectedPack();
    var prices = PACK_PRICES[pack];
    if (prices) {
      var otpEl = toggle.querySelector('[data-bundle-otp-price]');
      var subEl = toggle.querySelector('[data-bundle-sub-price]');
      var savEl = toggle.querySelector('[data-bundle-savings]');
      if (otpEl) otpEl.textContent = formatMoney(prices.onetime);
      if (subEl) subEl.textContent = formatMoney(prices.subscribe);
      if (savEl) {
        var saved = prices.onetime - prices.subscribe;
        savEl.textContent = saved > 0 ? 'Subscribe and save ' + formatMoney(saved) : '';
      }
    }

    // Show/hide frequency wrapper based on purchase type
    if (freqSlot) {
      freqSlot.style.display = (state.purchaseType === 'subscribe') ? '' : 'none';
    }

    // Update price on all Confirm/ATC buttons via data attribute (CSS ::after reads it)
    var btnPrice = prices
      ? formatMoney(state.purchaseType === 'subscribe' ? prices.subscribe : prices.onetime)
      : '';
    document.querySelectorAll('.ab-bundle-add-bundle-to-cart').forEach(function (btn) {
      btn.setAttribute('data-bb-price', btnPrice);
    });
  }

  /* ══════════════════════════════════════
     Cart Drawer — Refresh & Open
     ══════════════════════════════════════ */

  /**
   * Fetch fresh cart-drawer HTML from Shopify's section rendering API,
   * swap it into the existing <cart-drawer> element, then open the drawer
   * using Dawn's native .open() method.
   */
  function refreshAndOpenDrawer() {
    fetch('/?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (!html) return;

        var currentDrawer = document.querySelector('cart-drawer');
        if (!currentDrawer) return;

        // Parse fresh section HTML
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var freshDrawer = tmp.querySelector('cart-drawer');
        if (!freshDrawer) return;

        // Replace inner contents
        currentDrawer.innerHTML = freshDrawer.innerHTML;
        currentDrawer.classList.remove('is-empty');

        // Open using Dawn's native method on next frame
        // (allows DOM to settle after innerHTML swap)
        requestAnimationFrame(function () {
          if (typeof currentDrawer.open === 'function') {
            currentDrawer.open();
          } else {
            // Fallback: manually trigger visible state
            currentDrawer.classList.add('active');
            document.body.classList.add('overflow-hidden');
          }
        });

        // Update cart count bubble
        updateCartCount();
      })
      .catch(function (err) {
        console.error('[BB] refreshAndOpenDrawer failed:', err);
      });
  }

  /**
   * Sync the header cart count bubble with actual cart state.
   */
  function updateCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('.cart-count-bubble').forEach(function (bubble) {
          var span = bubble.querySelector('span[aria-hidden]');
          if (span) span.textContent = cart.item_count;
          if (cart.item_count > 0) bubble.classList.remove('hidden');
        });
      });
  }

  /* ══════════════════════════════════════
     Bundle Confirmation — Cart Polling
     ══════════════════════════════════════ */

  /**
   * Capture-phase listener on Appstle's "Confirm flavors" button.
   * When clicked, polls /cart.js to detect when Appstle finishes
   * adding items. Once cart count increases → mark confirmed + open drawer.
   *
   * This approach is Appstle-proof: doesn't rely on click events
   * bubbling (Appstle's React calls stopPropagation).
   */
  function initBundleConfirmWatcher() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.ab-bundle-add-bundle-to-cart');
      if (!btn || btn.disabled) return;
      if (!isMixMatchActive()) return;

      // Snapshot current cart count
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var prevCount = cart.item_count;
          var attempts = 0;
          var maxAttempts = 15; // 15 × 400ms = 6 seconds max

          var poller = setInterval(function () {
            attempts++;
            if (attempts > maxAttempts) {
              clearInterval(poller);
              console.warn('[BB] Bundle cart poll timed out');
              return;
            }

            fetch('/cart.js')
              .then(function (r) { return r.json(); })
              .then(function (newCart) {
                if (newCart.item_count > prevCount) {
                  clearInterval(poller);
                  state.bundleConfirmed = true;
                  refreshAndOpenDrawer();
                }
              });
          }, 400);
        });
    }, true); // capture phase — fires before Appstle's handler
  }

  /* ══════════════════════════════════════
     ATC Intercept — Unified
     ══════════════════════════════════════ */

  /**
   * Nudge user toward the bundle zone when they click ATC
   * without having confirmed their flavors.
   */
  function nudgeToBundleZone() {
    var bid = getActiveBundleId();
    if (!bid) return;

    var bundleEl = document.querySelector('[appstle-dynamic-bundle-unique-reference="' + bid + '"]');
    if (!bundleEl) return;

    // Scroll to bundle zone
    bundleEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Pulse the confirm button
    var confirmBtn = bundleEl.querySelector('.ab-bundle-add-bundle-to-cart');
    if (confirmBtn) {
      confirmBtn.classList.add('bb-pulse');
      setTimeout(function () { confirmBtn.classList.remove('bb-pulse'); }, 2000);
    }

    // Shake the validation message if visible
    var validation = bundleEl.querySelector('.ab-bundle-required-product-validation-message');
    if (validation && validation.offsetParent !== null) {
      validation.classList.add('bb-shake');
      setTimeout(function () { validation.classList.remove('bb-shake'); }, 600);
    }
  }

  /**
   * Single unified ATC intercept.
   *
   * Uses capture-phase submit listener so it fires before
   * product-form.js and survives Shopify section re-renders.
   *
   * In Mix & Match mode, the theme ATC NEVER submits the form
   * (parent variant should not be added). Instead:
   * - Not confirmed → nudge to bundle zone
   * - Confirmed → re-open cart drawer (items already in cart)
   */
  function initATCIntercept() {
    // Primary: intercept form submit
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
      if (!isMixMatchActive()) return;

      // Always block — parent variant must never be added for Mix & Match
      e.preventDefault();
      e.stopImmediatePropagation();

      if (state.bundleConfirmed) {
        refreshAndOpenDrawer();
      } else if (isBundleIncomplete()) {
        nudgeToBundleZone();
      } else {
        // Bundle is complete but user hasn't clicked Confirm yet
        nudgeToBundleZone();
      }
    }, true);

    // Backup: also intercept click on ATC button
    // (catches edge cases where click fires but submit doesn't)
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.product-form__submit');
      if (!btn) return;
      if (!isMixMatchActive()) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      if (state.bundleConfirmed) {
        refreshAndOpenDrawer();
      } else {
        nudgeToBundleZone();
      }
    }, true);
  }

  /* ══════════════════════════════════════
     Radio ID Helpers
     ══════════════════════════════════════ */

  function getSubscribeRadioId() {
    var el = document.querySelector('.bb-subscribe-sub input[type="radio"]');
    return el ? el.id : '';
  }

  function getOnetimeRadioId() {
    var el = document.querySelector('.bb-subscribe-onetime input[type="radio"]');
    return el ? el.id : '';
  }

  /* ══════════════════════════════════════
     Event Binding
     ══════════════════════════════════════ */

  function bindEvents() {
    // Pack radios
    document.querySelectorAll('.bb-pack-selector input[type="radio"]').forEach(function (radio) {
      if (boundElements.has(radio)) return;
      boundElements.add(radio);
      radio.addEventListener('change', function () {
        state.bundleConfirmed = false; // Reset on pack change
        ensureFlavourSelected();
        applyPrices();
      });
    });

    // Flavour radios
    document.querySelectorAll('.bb-flavour-selector input[type="radio"]').forEach(function (radio) {
      if (boundElements.has(radio)) return;
      boundElements.add(radio);
      radio.addEventListener('change', function () {
        state.flavour = radio.value;
        state.bundleConfirmed = false; // Reset on flavour change
        applyBundleDisplay();
      });
    });

    // Purchase type radios
    document.querySelectorAll('input[name="bb-purchase-type"]').forEach(function (radio) {
      if (boundElements.has(radio)) return;
      boundElements.add(radio);
      radio.addEventListener('change', function () {
        state.purchaseType = radio.value;
        state.bundleConfirmed = false; // Reset on OTP/Sub switch
        applyPrices();
      });
    });

    // Frequency dropdown
    document.querySelectorAll('[data-frequency-select]').forEach(function (sel) {
      if (boundElements.has(sel)) return;
      boundElements.add(sel);
      sel.addEventListener('change', function () {
        state.frequencyIndex = parseInt(sel.value, 10);
        applyPrices();
      });
    });

    // Learn more modal
    document.querySelectorAll('[data-learn-more]').forEach(function (btn) {
      if (boundElements.has(btn)) return;
      boundElements.add(btn);
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var modal = document.querySelector('[data-bb-subscription-modal]');
        if (modal) modal.removeAttribute('hidden');
      });
    });

    // Modal close
    document.querySelectorAll('[data-bb-modal-close]').forEach(function (el) {
      if (boundElements.has(el)) return;
      boundElements.add(el);
      el.addEventListener('click', function () {
        var modal = document.querySelector('[data-bb-subscription-modal]');
        if (modal) modal.setAttribute('hidden', '');
      });
    });

    // Quantity input
    document.querySelectorAll('quantity-input input[name="quantity"]').forEach(function (input) {
      if (boundElements.has(input)) return;
      boundElements.add(input);
      input.addEventListener('change', function () { applyPrices(); });
    });
    document.querySelectorAll('quantity-input button').forEach(function (btn) {
      if (boundElements.has(btn)) return;
      boundElements.add(btn);
      btn.addEventListener('click', function () {
        setTimeout(applyPrices, 50);
      });
    });
  }

  /* ══════════════════════════════════════
     Setup + Observer + Init
     ══════════════════════════════════════ */

  function setup() {
    state.flavour = getSelectedFlavour();
    bindEvents();
    ensureFlavourSelected();
    applyPrices(); // also calls applyBundleDisplay
  }

  var setupTimer = null;
  var pauseObserver = false;
  var observer = new MutationObserver(function () {
    if (pauseObserver) return;
    clearTimeout(setupTimer);
    setupTimer = setTimeout(function () {
      if (document.querySelector('.bb-subscribe-wrapper')) {
        setup();
      }
    }, 100);
  });

  function startObserver() {
    var target = document.querySelector('.product__info-wrapper')
      || document.querySelector('main')
      || document.body;
    observer.observe(target, { childList: true, subtree: true });
  }

  function init() {
    if (!document.querySelector('.bb-subscribe-wrapper')) return;
    setup();
    startObserver();
  }

  /* ── Register document-level intercepts (once, survive re-renders) ── */
  initATCIntercept();
  initBundleConfirmWatcher();

  /* ── Boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Auto-select 2 Pack + Subscribe on full page load ── */
  window.addEventListener('load', function () {
    setTimeout(function () {
      pauseObserver = true;

      var pack2 = document.querySelector('.bb-pack-selector input[value="2 Pack"]');
      if (pack2) {
        var label = pack2.nextElementSibling;
        if (label) label.click();
      }

      setTimeout(function () {
        var subRadio = document.querySelector('.bb-subscribe-sub input[type="radio"]');
        if (subRadio) subRadio.checked = true;
        state.purchaseType = 'subscribe';
        pauseObserver = false;
        setup();
      }, 800);
    }, 1500);
  });

})();
